import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useResolvedPath } from "react-router";

import { EmptyArray, EmptyObject } from "capitalrx-components-2.0";
import { AuthContext, AuthContextValue, AuthTokenProvider } from "capitalrx-shell-auth";
import classNames from "classnames";
import styles from "./Shell.module.css";

import { AsyncWrapper } from "./AsyncWrapper";
import { ContentLayout } from "./ContentLayout";
import { ErrorPage } from "./ErrorPage";
import { Routes } from "./Routes";
import { ChannelSubscriptionProvider, ChannelSubscriptionTable, DefaultProviderFunction, fetchAvailableModules, loadModules } from "./api";
import { initializeSharedLibraries as initializeSharedLibrariesApi } from "./api/initializeSharedLibraries";
import { ContentChannel } from "./components";
import { DynamicSubscriptionDeregistrationFunc, OwnerModuleContext, ShellContext, ShellContextModel } from "./context";
import { useEvent } from "./hooks";
import { HotkeyRegistryContext, createHotkeyRegistryManager } from "./hotkeyRegistry";
import { ComponentSubscription, ModuleManifest, Subscription } from "./model";
import { Body } from "capitalrx-components-2.0/lib/typography";
import { AlertProvider, OverlayContainer, TooltipContainer, HotKeyRoot } from "capitalrx-components-2.0/lib/general";

/**
 * Wrapper component that conditionally renders HotKeyRoot based on Layer 3 shell configuration.
 * This integrates with the module routing to set metadata and check permissions.
 */
const ShellHotkeyWrapper = ({ children }: { children: ReactNode }): ReactNode => {
  /* Note: In future, extract moduleId from current route
     For now, HotKeyRoot will check metadata set by modules */
  return <HotKeyRoot>{children}</HotKeyRoot>;
};

/** The shell loading state. */
export type LoadingState =
  | "initializeSharedLibraries"
  | "loadAvailableCoreModules"
  | "clearCoreModules"
  | "initializeCoreModules"
  | "loadAvailableAppModules"
  | "clearAppModules"
  | "initializeAppModules"
  | "renderRoutes";

/** The shell life cycle progression table. */
const LifeCycleProcess: Record<LoadingState, LoadingState | null> = {
  initializeSharedLibraries: "loadAvailableCoreModules",
  loadAvailableCoreModules: "clearCoreModules",
  clearCoreModules: "initializeCoreModules",
  initializeCoreModules: "loadAvailableAppModules",
  loadAvailableAppModules: "clearAppModules",
  clearAppModules: "initializeAppModules",
  initializeAppModules: "renderRoutes",
  renderRoutes: null,
};

/** The shell life cycle names. */
const LoadingStateNames: Record<LoadingState, string> = {
  initializeSharedLibraries: "Initialize Shared Libraries",
  loadAvailableCoreModules: "Fetch Core Modules",
  clearCoreModules: "Clear Core Modules",
  initializeCoreModules: "Initialize Core Modules",
  loadAvailableAppModules: "Fetch Application Modules",
  clearAppModules: "Clear Application Modules",
  initializeAppModules: "Initialize Application Modules",
  renderRoutes: "Render Routes",
};

/**
 * A function which registers additional libraries to be made available to modules.
 * @param name The name of the library to register, should correspond to the same name as when imported.
 * @param library The object representing the exports of the registered library.
 */
export type LibraryRegistrationFn = (name: string, library: object) => void;

/** A handler function for the shell `onLoadingStateChange` event. */
export type ShellLoadingStateChangeHandler = (state: LoadingState) => void;

/** A handler function for the shell `onInitializeSharedLibraries` event. */
export type ShellInitializeSharedLibrariesHandler = (register: LibraryRegistrationFn) => void;

/** The `Shell` component properties. */
export interface ShellProps {
  /** Event called when the shell loading state changes. */
  readonly onLoadingStateChange?: ShellLoadingStateChangeHandler;

  /** Event Called when the shell analytics ID changes. */
  readonly onAnalyticsIdChange?: (analyticsId: string | null) => void;

  /** Event called when the shell is collecting additional libraries to register for modules. */
  readonly onInitializeSharedLibraries?: ShellInitializeSharedLibrariesHandler;

  /** The component children. */
  readonly children?: () => ReactNode;
}

/** The main application shell component. */
export const Shell = (props: ShellProps): ReactNode => {
  const { children } = props;

  const path = useResolvedPath("").pathname;
  const loadingCountRef = useRef<number>(0);
  const coreModulePaths = useRef<ReadonlyArray<string> | null>(null);
  const appModulePaths = useRef<ReadonlyArray<string> | null>(null);
  const coreModules = useRef<ReadonlyArray<ModuleManifest> | null>(null);
  const appModules = useRef<ReadonlyArray<ModuleManifest> | null>(null);

  // Shell-level hotkey registry - single instance for entire shell lifecycle
  const hotkeyRegistryManager = useMemo(() => {
    const manager = createHotkeyRegistryManager();
    // Make registry available globally for cross-module access
    if (typeof window !== "undefined") {
      (window as any).__CAPITAL_RX_HOTKEY_REGISTRY__ = manager;
    }
    return manager;
  }, []);

  const deferredPermissions = useRef<ReadonlyArray<string> | null>(null);
  const [locked, setLocked] = useState(false);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [authRefreshToken, setAuthRefreshToken] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<ReadonlyArray<string>>([]);
  const [dynamicSubscriptions, setDynamicSubscriptions] = useState<ChannelSubscriptionTable>(EmptyObject as ChannelSubscriptionTable);
  const [analyticsId, setAnalyticsId] = useState<string | null>(null);
  const [currentLoadingStage, setCurrentLoadingStage] = useState<LoadingState>("initializeSharedLibraries");
  const [coreChannelSubscriptions, setCoreChannelSubscriptions] = useState<ChannelSubscriptionProvider | null>(
    () => DefaultProviderFunction,
  );
  const [appChannelSubscriptions, setAppChannelSubscriptions] = useState<ChannelSubscriptionProvider | null>(() => DefaultProviderFunction);
  const [hasError, setHasError] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const cancelLoadRef = useRef<(() => void) | null>(null);

  const onInitializeSharedLibraries = useEvent(props.onInitializeSharedLibraries);
  const onLoadingStateChange = useEvent(props.onLoadingStateChange);
  const onAnalyticsIdChange = useEvent(props.onAnalyticsIdChange);

  const loadFromStage = useCallback((stage: LoadingState) => {
    let cancel = false;

    async function waitForLoadingToComplete() {
      await new Promise<void>((resolve) => {
        function doCheck() {
          if (loadingCountRef.current === 0) {
            resolve();
          } else setTimeout(doCheck);
        }

        doCheck();
      });
    }

    /** Initializes the shared libraries. */
    function initializeSharedLibraries() {
      // eslint-disable-next-line no-undef
      for (const entry of System.entries()) {
        const [moduleId] = entry;
        // eslint-disable-next-line no-undef
        System.delete(moduleId);
      }
      try {
        initializeSharedLibrariesApi();
      } catch {
        setHasError(true);
        return;
      }

      onInitializeSharedLibraries((name, library) => {
        try {
          // eslint-disable-next-line no-undef
          System.register(name, [], (_export) => {
            _export(library);
            _export("default", library);
            return {};
          });
        } catch {
          setHasError(true);
        }
      });
    }

    /** Loads the available core modules. */
    async function loadAvailableCoreModules() {
      coreModulePaths.current = await fetchAvailableModules("core");
    }

    /** Loads the available app modules. */
    async function loadAvailableAppModules() {
      appModulePaths.current = await fetchAvailableModules("app");
    }

    /** Clears the core modules. */
    function clearCoreModules() {
      // eslint-disable-next-line no-undef
      for (const path of coreModulePaths.current || []) System.delete(path);
      coreModules.current = null;
    }

    /** Clears the app modules. */
    function clearAppModules() {
      // eslint-disable-next-line no-undef
      for (const path of appModulePaths.current || []) System.delete(path);
      appModules.current = null;
    }

    /** Initializes the core modules. */
    async function initializeCoreModules() {
      try {
        const [newModules, newChannelSubscriptions] = await loadModules("core", coreModulePaths.current, null);
        if (cancel) return;

        coreModules.current = newModules;

        setCoreChannelSubscriptions(() => newChannelSubscriptions);
      } catch {
        setHasError(true);
      }
    }

    /** Initializes the app modules. */
    async function initializeAppModules() {
      const permissionsToUse = deferredPermissions.current ? deferredPermissions.current : permissions;

      // Clear whatever deferred permissions we have right now.
      deferredPermissions.current = null;

      const [newModules, newChannelSubscriptions] = await loadModules("app", appModulePaths.current, permissionsToUse);
      if (cancel) return;

      appModules.current = newModules;
      setAppChannelSubscriptions(() => newChannelSubscriptions);
    }

    async function runStage(stage: LoadingState): Promise<void> {
      await waitForLoadingToComplete();
      if (cancel) return;

      console.log(`[Shell] ${LoadingStateNames[stage]}`);
      sessionStorage.setItem("_shell_ls", stage);

      switch (stage) {
        case "initializeSharedLibraries":
          initializeSharedLibraries();
          break;
        case "loadAvailableCoreModules":
          await loadAvailableCoreModules();
          break;
        case "loadAvailableAppModules":
          await loadAvailableAppModules();
          break;
        case "clearCoreModules":
          clearCoreModules();
          break;
        case "initializeCoreModules":
          await initializeCoreModules();
          break;
        case "clearAppModules":
          clearAppModules();
          break;
        case "initializeAppModules":
          await initializeAppModules();
          break;
        case "renderRoutes":
          break;
        default:
          break;
      }
    }

    async function run() {
      let currentStage: LoadingState | null = stage;

      while (currentStage !== null) {
        await runStage(currentStage);
        if (cancel) return;

        setCurrentLoadingStage(currentStage);
        onLoadingStateChange(currentStage);

        if (deferredPermissions.current && currentStage === "renderRoutes") currentStage = "clearAppModules";
        else currentStage = LifeCycleProcess[currentStage];
      }
    }

    run();

    return () => {
      cancel = true;
    };
  }, []);

  /* If permissions change and we're already rendering the routes, move back to clearAppModules.
     Skip this during logout (when authToken becomes null) to prevent race conditions. */
  useEffect(() => {
    /* Guard: Don't trigger module reload when logging out (authToken is null)
       This prevents a race condition where clearing permissions triggers clearAppModules
       while the logout navigation is still in progress.
       Note: authToken and isLoggingOut are intentionally NOT in the dependency array
       because we only want this effect to run on permissions changes, not auth state changes. */
    if (authToken === null || isLoggingOut) {
      return;
    }

    deferredPermissions.current = permissions;

    if (currentLoadingStage === "renderRoutes") {
      if (cancelLoadRef.current) {
        cancelLoadRef.current();
        cancelLoadRef.current = null;
      }

      cancelLoadRef.current = loadFromStage("clearAppModules");

      return () => {
        if (!cancelLoadRef.current) return;
        cancelLoadRef.current();
        cancelLoadRef.current = null;
      };
    }
  }, [permissions]);

  // Reset isLoggingOut flag when a new auth token is set (successful login after logout)
  useEffect(() => {
    if (authToken !== null && isLoggingOut) {
      setIsLoggingOut(false);
    }
  }, [authToken, isLoggingOut]);

  useEffect(() => {
    if (cancelLoadRef.current) {
      cancelLoadRef.current();
      cancelLoadRef.current = null;
    }

    cancelLoadRef.current = loadFromStage("initializeSharedLibraries");

    return () => {
      if (!cancelLoadRef.current) return;
      cancelLoadRef.current();
      cancelLoadRef.current = null;
    };
  }, [props.onInitializeSharedLibraries]);

  useEffect(() => {
    onAnalyticsIdChange(analyticsId);
  }, [props.onAnalyticsIdChange, analyticsId]);

  const incrementLoadingCountHandler = useCallback((): void => {
    if (loadingCountRef.current === 0) {
      console.log("[Shell] Life Cycle Locked, Waiting...");
      setLocked(true);
    }

    loadingCountRef.current++;
  }, []);

  const decrementLoadingCountHandler = useCallback((): void => {
    if (loadingCountRef.current === 0) return;
    loadingCountRef.current--;

    if (loadingCountRef.current === 0) {
      console.log("[Shell] Life Cycle Unlocked, Resuming");
      setLocked(false);
    }
  }, []);

  const modules = useMemo(() => [...(appModules.current || []), ...(coreModules.current || [])], [coreModules.current, appModules.current]);
  const rootModule = useMemo(() => (modules ? modules.filter((module) => !!module.index)[0] || null : null), [modules]);

  const registerDynamicChannelSubscription = useCallback(
    (moduleId: string | null, channelId: string, subscription: ComponentSubscription): DynamicSubscriptionDeregistrationFunc => {
      if (moduleId === null) moduleId = "_null_";
      const storedModuleId = moduleId === null ? "_null_" : moduleId;

      const { module: info, component: Component } = subscription;
      const module = modules.find((module) => module.info === info);
      if (!module) {
        return (): void => {
          /* do nothing */
        };
      }

      const containerProvider = async () => {
        switch (module.version) {
          case 1:
          case undefined:
            return module.container;
          case 2:
            return await module.container?.();
          default:
            return undefined;
        }
      };

      const wrappedComponent = (): JSX.Element => (
        <OwnerModuleContext.Provider value={info}>
          <AsyncWrapper resolver={containerProvider}>
            <Component />
          </AsyncWrapper>
        </OwnerModuleContext.Provider>
      );

      const finalSubscription = {
        ...subscription,
        component: wrappedComponent,
      };

      setDynamicSubscriptions((prev) => ({
        ...prev,
        [storedModuleId]: {
          ...prev?.[storedModuleId],
          [channelId]: [...(prev?.[storedModuleId]?.[channelId] || []), finalSubscription],
        },
      }));

      return (): void => {
        setDynamicSubscriptions((prev) => ({
          ...prev,
          [storedModuleId]: {
            ...prev?.[storedModuleId],
            [channelId]: prev?.[storedModuleId]?.[channelId]?.filter((x) => x !== finalSubscription),
          },
        }));
      };
    },
    [modules],
  );

  const getDynamicChannelSubscriptions = useCallback(
    (moduleId: string | null, channelId: string): ReadonlyArray<Subscription> => {
      if (moduleId === null) moduleId = "_null_";

      const channels = dynamicSubscriptions[moduleId];
      if (!channels) return EmptyArray as ReadonlyArray<Subscription>;
      return channels[channelId] || (EmptyArray as ReadonlyArray<Subscription>);
    },
    [dynamicSubscriptions],
  );

  const authContextValue = useMemo<AuthContextValue>(() => {
    return {
      authToken,
      authRefreshToken,
      setAuthToken,
      setAuthRefreshToken,
      permissions,
      setPermissions,
    };
  }, [authToken, authRefreshToken, permissions]);

  const notifyLogoutStartedHandler = useCallback((): void => {
    setIsLoggingOut(true);
    // Cancel any in-progress module loading operations
    if (cancelLoadRef.current) {
      cancelLoadRef.current();
      cancelLoadRef.current = null;
    }
  }, []);

  const shellContextValue = useMemo<ShellContextModel>(() => {
    let trimmedPath = path;
    if (path.endsWith("/")) trimmedPath = path.substr(0, path.length - 1);

    return {
      rootPath: path,
      moduleRootPath: `${trimmedPath}/module`,
      rootModule: rootModule ? rootModule.info || null : null,
      modules,
      incrementLoadingCount: incrementLoadingCountHandler,
      decrementLoadingCount: decrementLoadingCountHandler,
      getChannelSubscriptions: (moduleId, channelId): ReadonlyArray<Subscription> => {
        const staticSubscriptions = [
          ...(appChannelSubscriptions ? appChannelSubscriptions(moduleId, channelId) : []),
          ...(coreChannelSubscriptions ? coreChannelSubscriptions(moduleId, channelId) : []),
        ];

        // The total set of static subscriptions need to be sorted based on their descending priority.
        staticSubscriptions.sort((a, b) => a.priority - b.priority);

        return [...staticSubscriptions, ...getDynamicChannelSubscriptions(moduleId, channelId)];
      },
      registerDynamicChannelSubscription,
      setAnalyticsId: (analyticsId): void => {
        setAnalyticsId(analyticsId || null);
      },
      isLoggingOut,
      notifyLogoutStarted: notifyLogoutStartedHandler,
    };
  }, [
    path,
    modules,
    rootModule,
    coreChannelSubscriptions,
    appChannelSubscriptions,
    getDynamicChannelSubscriptions,
    registerDynamicChannelSubscription,
    incrementLoadingCountHandler,
    decrementLoadingCountHandler,
    setAnalyticsId,
    isLoggingOut,
    notifyLogoutStartedHandler,
  ]);

  return (
    <TooltipContainer>
      <OverlayContainer>
        <HotkeyRegistryContext.Provider value={hotkeyRegistryManager}>
          <ShellHotkeyWrapper>
            <Body>
              <AuthContext.Provider value={authContextValue}>
                <AuthTokenProvider authToken={authToken}>
                  <ShellContext.Provider value={shellContextValue}>
                    {!hasError && (
                      <AlertProvider>
                        <div className={classNames(styles.lifeCycleIndicator, "life-cycle")}>{currentLoadingStage}</div>
                        {currentLoadingStage !== "renderRoutes" ? (
                          <Shell.Splash locked={locked} loadingState={currentLoadingStage} />
                        ) : (
                          <Routes>{children}</Routes>
                        )}
                        <ContentChannel id="global" />
                      </AlertProvider>
                    )}
                    {hasError && (
                      <ContentLayout>
                        <ErrorPage />
                      </ContentLayout>
                    )}
                  </ShellContext.Provider>
                </AuthTokenProvider>
              </AuthContext.Provider>
            </Body>
          </ShellHotkeyWrapper>
        </HotkeyRegistryContext.Provider>
      </OverlayContainer>
    </TooltipContainer>
  );
};

/** The `Shell.Splash` component properties. */
interface SplashProps {
  /** The current shell loading state. */
  readonly loadingState: LoadingState;

  /** Indicates whether shell loading currently locked. */
  readonly locked: boolean;
}

/** The shell splash screen. */
Shell.Splash = (props: SplashProps): ReactNode => {
  const { loadingState, locked } = props;

  return (
    <div className={styles.splashRoot}>
      <div className={styles.splashContent}>
        <div className={styles.splashSpinner}>
          <div className={styles.dot1} />
          <div className={styles.dot2} />
        </div>

        {loadingState !== "renderRoutes" && process.env.NODE_ENV !== "production" && (
          <div className={classNames(styles.loadingStatus, "loading-status", { [styles.locked]: locked }, styles[loadingState])} />
        )}
      </div>
    </div>
  );
};
